<template>
  <div class="form-renderer">
    <form @submit.prevent="handleSubmit">
      <div v-for="container in formStructure.containers" :key="container.uid">
        <div :style="applyInlineStyles(container.design)" v-show="isVisible(container)">
          <div class="row g-2">
            <div
              v-for="column in container.columns"
              :key="column.uid"
              :class="`col-md-${column.width || 12}`"
            >
              <div :style="applyInlineStyles(column.design)" v-show="isVisible(column)">
                <div
                  v-for="element in column.elements"
                  :key="element.uid"
                  class="mb-3"
                  v-show="isVisible(element)"
                >
                  <RenderedElement
                    v-if="element.type === 'element'"
                    :element="element"
                    :answers="answers"
                    :read-only="props.readOnly"
                    @update="handleElementUpdate"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { FormStructure, FormAnswer, FormComponent, LogicRule } from '../types/form-builder';
import { applyInlineStyles } from '../utils/form-helpers';
import RenderedElement from './renderer/RenderedElement.vue';

interface Props {
  formStructure: FormStructure;
  initialAnswers?: FormAnswer;
  readOnly?: boolean;
}

interface Emits {
  (e: 'submit', answers: FormAnswer): void;
  (e: 'update', answers: FormAnswer): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const answers = ref<FormAnswer>(props.initialAnswers || {});


function handleElementUpdate(fieldId: string, value: any) {
  answers.value[fieldId] = value;
  emit('update', answers.value);
}

function isVisible(component: FormComponent): boolean {
  if (!component.logic || component.logic.length === 0) {
    return true;
  }

  for (const rule of component.logic) {
    const ruleMatches = evaluateRule(rule);

    if (ruleMatches) {
      if (rule.action === 'hide') {
        return false;
      } else if (rule.action === 'show') {
        return true;
      }
    }
  }

  return true;
}

function evaluateRule(rule: LogicRule): boolean {
  if (rule.conditions.length === 0) return false;

  const results = rule.conditions.map((condition) => {
    const fieldValue = answers.value[condition.field];
    const compareValue = condition.value;

    switch (condition.operator) {
      case 'equals':
        return String(fieldValue) === String(compareValue);
      case 'notEquals':
        return String(fieldValue) !== String(compareValue);
      case 'contains':
        return String(fieldValue).includes(String(compareValue));
      case 'greaterThan':
        return Number(fieldValue) > Number(compareValue);
      case 'lessThan':
        return Number(fieldValue) < Number(compareValue);
      default:
        return false;
    }
  });

  if (rule.conjunction === 'and') {
    return results.every((r) => r);
  } else {
    return results.some((r) => r);
  }
}

function handleSubmit() {
  emit('submit', answers.value);
}
</script>

<style scoped>
.form-renderer {
  padding: 20px;
}
</style>
